/* eslint-disable react/require-default-props */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import FlyoutTabItemCss from "./FlyoutTabItem.css";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import checkmarkIcon from "dc-icons/frictionless/s_checkmark_blue_12.svg";
import withToolTip from "dc-preview-utils/src/HOC/withToolTip";

const withShortcutLabel = (txt, shortcutLabel) => (shortcutLabel ? `${txt} (${shortcutLabel})` : txt);

const FlyoutTabItem = props => {
  const { icon, label, verbClickHandler, isSelected, cursorType, shortcutLabel } = props;

  let tabItemClassName = FlyoutTabItemCss.TabItem;

  const cursor = useMemo(() => (cursorType ? { cursor: cursorType } : null), [cursorType]);

  return (
    <div
      className={tabItemClassName}
      role="tab"
      tabIndex={-1}
      aria-label={withShortcutLabel(label, shortcutLabel)}
      aria-selected={isSelected ? "true" : "false"}
      onKeyPress={e => (e.which === codes.ENTER || e.which === codes.SPACE ? verbClickHandler(e) : null)}
      onClick={e => verbClickHandler(e)}
      style={cursor}
      data-test-id={`flyout-${withShortcutLabel(label, shortcutLabel)}`}
    >
      <div className={FlyoutTabItemCss.iconAndLabel}>
        {icon && (
          <div className={FlyoutTabItemCss.icon}>
            <CustomIcon url={icon} />
          </div>
        )}
        <div className={FlyoutTabItemCss.LabelDiv}>
          <div className={FlyoutTabItemCss.Label}>{label}</div>
        </div>
      </div>
      {isSelected ? (
        <div className={FlyoutTabItemCss.checkmarkIcon}>
          <CustomIcon url={checkmarkIcon} />
        </div>
      ) : null}
    </div>
  );
};

FlyoutTabItem.propTypes = {
  icon: PropTypes.elementType,
  verbClickHandler: PropTypes.func.isRequired,
  label: PropTypes.string,
  isSelected: PropTypes.bool,
  cursorType: PropTypes.string,
  shortcutLabel: PropTypes.string
};

FlyoutTabItem.defaultProps = {
  label: "",
  isSelected: false,
  cursorType: null,
  shortcutLabel: ""
};

export default withToolTip(FlyoutTabItem);
